import { useMemo, useState } from 'react';
import { BUILD_STAMP, CONFIG } from '../config';
import { Dialog } from './Dialog';

interface Props {
  readonly elementCount: number;
  readonly totalCount: number;
  readonly zoom: number;
  readonly storageMode: 'indexeddb' | 'memory';
  readonly syncAvailable: boolean;
  readonly boardName: string;
  readonly onClose: () => void;
}

/**
 * Everything needed to explain a rendering or input problem, and nothing that
 * reveals board contents: counts and sizes only, never element data or text.
 */
function collect(props: Props): string {
  const canvas = document.querySelector<HTMLCanvasElement>('#board-canvas');
  const host = canvas?.parentElement;
  const rect = host?.getBoundingClientRect();
  let contextOk = 'no canvas element';
  if (canvas) {
    try {
      contextOk = canvas.getContext('2d') ? 'yes' : 'NO (this would explain a blank board)';
    } catch (error) {
      contextOk = `threw: ${error instanceof Error ? error.name : 'unknown'}`;
    }
  }
  const lines = [
    `build stamp        ${BUILD_STAMP}`,
    `app title          ${CONFIG.appTitle}`,
    `user agent         ${navigator.userAgent}`,
    `platform           ${navigator.platform}`,
    `window             ${window.innerWidth} x ${window.innerHeight}`,
    `devicePixelRatio   ${window.devicePixelRatio}`,
    `canvas host box    ${rect ? `${Math.round(rect.width)} x ${Math.round(rect.height)}` : 'unknown'}`,
    `canvas css size    ${canvas ? `${canvas.style.width || 'unset'} x ${canvas.style.height || 'unset'}` : 'unknown'}`,
    `canvas pixel size  ${canvas ? `${canvas.width} x ${canvas.height}` : 'unknown'}`,
    `2d context         ${contextOk}`,
    `board              ${props.boardName}`,
    `elements visible   ${props.elementCount}`,
    `elements total     ${props.totalCount}`,
    `zoom               ${Math.round(props.zoom * 100)}%`,
    `storage            ${props.storageMode}`,
    `BroadcastChannel   ${props.syncAvailable ? 'available' : 'unavailable'}`,
    `Path2D             ${typeof Path2D === 'undefined' ? 'MISSING' : 'available'}`,
    `ResizeObserver     ${typeof ResizeObserver === 'undefined' ? 'MISSING' : 'available'}`,
    `crypto.randomUUID  ${typeof crypto?.randomUUID === 'function' ? 'available' : 'unavailable (fallback in use)'}`,
  ];
  return lines.join('\n');
}

export function DiagnosticsDialog(props: Props) {
  const report = useMemo(() => collect(props), [props]);
  const [copied, setCopied] = useState(false);

  return (
    <Dialog
      title="Diagnostics"
      onClose={props.onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(report)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
          >
            {copied ? 'Copied' : 'Copy report'}
          </button>
          <button type="button" className="btn" onClick={props.onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="prose">
        <p>
          Counts and sizes only. Nothing here includes what is on your boards. Useful when the
          canvas looks blank or drawing does not respond.
        </p>
      </div>
      <pre
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 12px',
          margin: 0,
          overflowX: 'auto',
        }}
      >
        {report}
      </pre>
    </Dialog>
  );
}
